import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { movies } from "@/mocks/movies";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { useToast } from "@/components/feature/ToastProvider";
import MovieGrid from "@/components/feature/MovieGrid";
import Breadcrumb from "@/components/feature/Breadcrumb";

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { showToast } = useToast();
  const query = searchParams.get("q") || "";
  const [keyword, setKeyword] = useState(query);

  useEffect(() => {
    setKeyword(query);
  }, [query]);

  useDocumentTitle(
    query ? `搜索"${query}" - 鸭王在线观看` : "搜索影片 - 鸭王在线观看",
    query
      ? `鸭王在线观看搜索结果：与"${query}"相关的电影资源，快速找到你想看的影片。`
      : "鸭王在线观看搜索页，输入关键词快速检索海量电影资源。"
  );

  const hotMovies = useMemo(
    () => [...movies].sort((a, b) => b.views - a.views).slice(0, 6),
    []
  );

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return movies.filter(
      (m) =>
        m.title.toLowerCase().includes(q) ||
        m.region.toLowerCase().includes(q) ||
        m.genres.some((g) => g.includes(q)) ||
        m.logline.toLowerCase().includes(q)
    );
  }, [query]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    if (q) {
      setSearchParams({ q });
    } else {
      setSearchParams({});
    }
  };

  const hasQuery = query.trim().length > 0;

  return (
    <div className="mx-auto w-full max-w-[1280px] px-4 py-6 md:px-6 md:py-10">
      <Breadcrumb items={[{ label: "搜索影片" }]} />

      <h1 className="mt-5 font-heading text-2xl font-extrabold text-foreground-950 md:text-3xl">
        搜索影片
      </h1>
      <p className="mt-1 text-sm text-foreground-600">
        输入影片名称、类型或地区，快速找到你想看的电影
      </p>

      <form
        onSubmit={handleSubmit}
        className="mt-5 flex h-12 w-full max-w-2xl items-center gap-2 rounded-full border border-background-300/60 bg-background-100 px-4 md:h-14"
      >
        <i className="ri-search-line text-lg text-foreground-500"></i>
        <input
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
          placeholder="例如：科幻、喜剧、中国大陆"
          className="flex-1 bg-transparent text-sm text-foreground-950 placeholder:text-foreground-500 focus:outline-none md:text-base"
        />
        {keyword && (
          <button
            type="button"
            onClick={() => {
              setKeyword("");
              setSearchParams({});
            }}
            className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-full text-foreground-500 transition-colors hover:text-foreground-950"
            aria-label="清空"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        )}
        <button
          type="submit"
          className="flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-full bg-accent-500 px-4 py-2 text-sm font-semibold text-background-950 transition-colors hover:bg-accent-400 md:px-6"
        >
          搜索
        </button>
      </form>

      {hasQuery ? (
        <section className="mt-8">
          <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-foreground-700">
              关键词「<span className="font-semibold text-accent-500">{query}</span>」共找到{" "}
              <span className="font-semibold text-accent-500">{results.length}</span> 个结果
            </p>
            <button
              type="button"
              onClick={() => showToast("刷新功能演示中，待接入")}
              className="flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-full border border-background-300/60 px-4 py-1.5 text-sm text-foreground-700 transition-colors hover:border-accent-500 hover:text-accent-500"
            >
              <i className="ri-refresh-line text-base"></i>
              刷新结果
            </button>
          </div>

          {results.length > 0 ? (
            <MovieGrid movies={results} />
          ) : (
            <div className="flex flex-col items-center gap-4 rounded-lg border border-background-200/60 bg-background-100 py-16 text-center">
              <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200/60 text-3xl text-foreground-500">
                <i className="ri-search-eye-line"></i>
              </span>
              <p className="text-base text-foreground-800">
                没有找到与「{query}」相关的影片
              </p>
              <p className="text-sm text-foreground-600">
                换个关键词试试，或看看下面的热门推荐
              </p>
            </div>
          )}
        </section>
      ) : (
        <section className="mt-8">
          <div className="mb-5 flex items-center gap-2 text-sm text-foreground-600">
            <i className="ri-fire-line text-lg text-accent-500"></i>
            还没有输入关键词，先看看大家都在追的热门影片
          </div>
        </section>
      )}

      {(results.length === 0) && (
        <section className={hasQuery ? "mt-10" : "mt-2"}>
          <div className="mb-4 flex items-center gap-3">
            <span className="h-6 w-1.5 rounded-full bg-accent-500"></span>
            <h2 className="font-heading text-xl font-bold text-foreground-950">热门推荐</h2>
          </div>
          <MovieGrid movies={hotMovies} />
        </section>
      )}

      {!hasQuery && (
        <div className="mt-4">
          <Link
            to="/category/action"
            className="inline-flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className="ri-apps-2-line"></i>
            浏览全部分类
          </Link>
        </div>
      )}
    </div>
  );
}